import type {
  FleetStatus,
  Deployment,
  Repository,
  DeployRequest,
  RegisterRepoRequest,
  Ticket,
  CreateTicketRequest,
  CreateTicketResponse,
  QueryTicketsRequest,
  QueryTicketsResponse,
  WorkflowStatus,
  WorkflowsResponse,
  FeaturePack,
} from '../types/api';
import { environment } from '../config/environment';

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = environment.apiBaseUrl) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    return response.json() as Promise<T>;
  }

  // ===== Fleet Management =====

  /**
   * Get fleet status and health
   */
  async getFleetStatus(): Promise<FleetStatus> {
    return this.request<FleetStatus>('/api/fleet/status');
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string; repos: number; tickets: number }> {
    return this.request<{ status: string; repos: number; tickets: number }>('/health');
  }

  // ===== Repository Management =====

  /**
   * Get all repositories
   */
  async getRepositories(): Promise<Repository[]> {
    const response = await this.request<{ repos: Repository[] }>('/api/repos');
    return response.repos;
  }

  /**
   * Get a specific repository by full name
   */
  async getRepository(fullName: string): Promise<Repository> {
    return this.request<Repository>(`/api/repos/${encodeURIComponent(fullName)}`);
  }

  /**
   * Register a new repository
   */
  async registerRepository(request: RegisterRepoRequest): Promise<Repository> {
    return this.request<Repository>('/api/repos/register', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Sync repository data from GitHub
   */
  async syncRepository(fullName: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/repos/sync/${encodeURIComponent(fullName)}`, {
      method: 'POST',
    });
  }

  /**
   * Update repository tags
   */
  async updateRepositoryTags(fullName: string, tags: string[]): Promise<Repository> {
    return this.request<Repository>(`/api/repos/${encodeURIComponent(fullName)}/tags`, {
      method: 'PUT',
      body: JSON.stringify({ tags }),
    });
  }

  // ===== Feature Deployment =====

  /**
   * Get all deployments
   */
  async getDeployments(filters?: {
    status?: string;
    repoName?: string;
    limit?: number;
  }): Promise<Deployment[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.repoName) params.append('repoName', filters.repoName);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const query = params.toString();
    const endpoint = query ? `/api/deployments?${query}` : '/api/deployments';

    return this.request<Deployment[]>(endpoint);
  }

  /**
   * Get a specific deployment by ID
   */
  async getDeployment(deploymentId: string): Promise<Deployment> {
    return this.request<Deployment>(`/api/deployments/${deploymentId}`);
  }

  /**
   * Deploy a feature to repositories
   */
  async deployFeature(request: DeployRequest): Promise<Deployment> {
    return this.request<Deployment>('/api/deploy', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Cancel a deployment
   */
  async cancelDeployment(deploymentId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/deployments/${deploymentId}/cancel`, {
      method: 'POST',
    });
  }

  /**
   * Rollback a deployment
   */
  async rollbackDeployment(deploymentId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/deployments/${deploymentId}/rollback`, {
      method: 'POST',
    });
  }

  // ===== Feature Pack Management =====

  /**
   * Get all feature packs
   */
  async getFeaturePacks(): Promise<FeaturePack[]> {
    const response = await this.request<{ features: FeaturePack[] }>('/api/features');
    return response.features;
  }

  /**
   * Get a specific feature pack
   */
  async getFeaturePack(name: string, version?: string): Promise<FeaturePack> {
    const endpoint = version
      ? `/api/features/${encodeURIComponent(name)}/${version}`
      : `/api/features/${encodeURIComponent(name)}`;
    return this.request<FeaturePack>(endpoint);
  }

  /**
   * Create or update a feature pack
   */
  async createFeaturePack(featurePack: FeaturePack): Promise<FeaturePack> {
    return this.request<FeaturePack>('/api/features', {
      method: 'POST',
      body: JSON.stringify(featurePack),
    });
  }

  /**
   * Delete a feature pack
   */
  async deleteFeaturePack(name: string, version?: string): Promise<{ message: string }> {
    const endpoint = version
      ? `/api/features/${encodeURIComponent(name)}/${version}`
      : `/api/features/${encodeURIComponent(name)}`;
    return this.request<{ message: string }>(endpoint, {
      method: 'DELETE',
    });
  }

  // ===== Ticket Management =====

  /**
   * Get all tickets with optional filters
   */
  async getTickets(request?: QueryTicketsRequest): Promise<QueryTicketsResponse> {
    const params = new URLSearchParams();
    if (request?.status) params.append('status', request.status);
    if (request?.priority) params.append('priority', request.priority);
    if (request?.complexity) params.append('complexity', request.complexity);
    if (request?.repoName) params.append('repoName', request.repoName);
    if (request?.assignee) params.append('assignee', request.assignee);
    if (request?.tags) request.tags.forEach(tag => params.append('tags', tag));
    if (request?.limit) params.append('limit', request.limit.toString());
    if (request?.offset) params.append('offset', request.offset.toString());

    const query = params.toString();
    const endpoint = query ? `/api/tickets?${query}` : '/api/tickets';

    return this.request<QueryTicketsResponse>(endpoint);
  }

  /**
   * Get a specific ticket by ID
   */
  async getTicket(ticketId: string): Promise<Ticket> {
    return this.request<Ticket>(`/api/tickets/${ticketId}`);
  }

  /**
   * Create tickets across repositories
   */
  async createTickets(request: CreateTicketRequest): Promise<CreateTicketResponse> {
    return this.request<CreateTicketResponse>('/api/tickets/create', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Update a ticket
   */
  async updateTicket(ticketId: string, updates: Partial<Ticket>): Promise<Ticket> {
    return this.request<Ticket>(`/api/tickets/${ticketId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete a ticket
   */
  async deleteTicket(ticketId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/tickets/${ticketId}`, {
      method: 'DELETE',
    });
  }

  // ===== Workflow Management =====

  /**
   * Get all workflows
   */
  async getWorkflows(): Promise<WorkflowStatus[]> {
    const response = await this.request<WorkflowsResponse>('/api/workflows');
    return response.workflows;
  }

  /**
   * Get a specific workflow by ID
   */
  async getWorkflowStatus(workflowId: string): Promise<WorkflowStatus> {
    return this.request<WorkflowStatus>(`/api/workflows/${workflowId}`);
  }

  /**
   * Cancel a workflow
   */
  async cancelWorkflow(workflowId: string): Promise<{ message: string; workflowId: string }> {
    return this.request<{ message: string; workflowId: string }>(`/api/workflows/${workflowId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Retry a failed workflow
   */
  async retryWorkflow(workflowId: string): Promise<WorkflowStatus> {
    return this.request<WorkflowStatus>(`/api/workflows/${workflowId}/retry`, {
      method: 'POST',
    });
  }
}

// Create a singleton instance
export const apiService = new ApiService();

// Export the class for testing or custom instances
export { ApiService };
